import api from "../utils/api-axios";

/**
 * Geocoding API — wraps the backend's location search endpoints.
 *
 * The actual reverse/forward geocoding happens on the backend (Nominatim),
 * not in the browser. This file just calls our backend.
 */

/**
 * Search for locations by text. Returns autocomplete suggestions.
 *
 * @param {string} query        what the user typed (min 2 chars)
 * @param {string} countryCode  ISO 2-letter code to bias results (e.g. "ZA")
 * @returns {Promise<Array<{coordinates: [number, number], label: string, addressParts: object, displayName: string}>>}
 */
export const searchLocations = async (query, countryCode = "ZA") => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    const response = await api.get("/user/location/search", {
      params: { q: query.trim(), country: countryCode },
    });

    if (response.success && Array.isArray(response.results)) {
      return response.results;
    }
    return [];
  } catch (error) {
    console.error("Location search failed:", error);
    return [];
  }
};

/**
 * Get the user's current position via the browser geolocation API.
 *
 * @returns {Promise<{lng: number, lat: number, accuracy: number}>}
 * @throws  if permission denied, unavailable, or timed out
 */
export const getBrowserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lng: position.coords.longitude,
          lat: position.coords.latitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        // Translate the cryptic browser errors into something usable
        let message = "Could not get your location";
        if (error.code === 1) message = "Location permission denied";
        else if (error.code === 2) message = "Location unavailable";
        else if (error.code === 3) message = "Location request timed out";

        const err = new Error(message);
        err.code = error.code;
        reject(err);
      },
      {
        enableHighAccuracy: false, // GPS-style accuracy not needed for radius search
        timeout: 10000,
        maximumAge: 5 * 60 * 1000, // accept cached position up to 5 min old
      }
    );
  });
};

/**
 * Reverse-geocode coords to a label without saving anything to the user's
 * profile. Used by LocationPicker to preview a label after the user clicks
 * "Use my current location."
 *
 * @param {number} lng
 * @param {number} lat
 * @returns {Promise<{label: string|null, addressParts: object|null}>}
 */
export const reverseGeocodeCoords = async (lng, lat) => {
  try {
    const response = await api.get("/user/location/reverse", {
      params: { lng, lat },
    });

    if (response.success) {
      return {
        label: response.label || null,
        addressParts: response.addressParts || null,
      };
    }
    return { label: null, addressParts: null };
  } catch (error) {
    console.error("Reverse geocode failed:", error);
    return { label: null, addressParts: null };
  }
};